// core
import { Injectable } from '@angular/core';

// Raptor
import { RaptorQuestionService } from '../services/raptor-question.service';
import { RaptorStepDataService } from './raptor-step-data.service';
import { 
  RAPTOR_EMAIL_ADDRESS,
  RAPTOR_MOBILE_NUMBER_AU, 
  RAPTOR_TELEPHONE_NUMBER_AU, 
  RAPTOR_MOBILE_NUMBER_NZ, 
  RAPTOR_TELEPHONE_NUMBER_NZ,
  RAPTOR_MOBILE_NUMBER_UK, 
  RAPTOR_TELEPHONE_NUMBER_INTERNATIONAL,
  RAPTOR_AHM_MEDIBANK_MEMBER_NUMBER,
  RAPTOR_PREDICATE_VARIABLES,
  RAPTOR_PREDICATE_CURRENT_DATE
} from '../constant/raptor-constants';

@Injectable()
export class RaptorPredicateService {

  constructor(
    private raptorQuestionService: RaptorQuestionService,
    private raptorStepDataService: RaptorStepDataService
  ) { }

  isDataDefined(data: any): boolean {
    return data !== null && data !== undefined;
  }

  getQuestionValue(formValues: any, key: string, mockValues: any) {
    let questionKeys: any = key.split('.');
    let questionKey = questionKeys[0];
    let formData = this.raptorStepDataService.get();
    let question = this.raptorQuestionService.getQuestionByKey(formData, questionKey);
    let questionValue: any;

    if (question && question.type === 'repeater') {  
      if (question.layout === 'multiple') {
        this.mountRepeaterData(formValues, formData, question);
      }
      questionValue = question.data;
    } else if (mockValues && this.isDataDefined(mockValues[questionKey])) {
      questionValue = mockValues[questionKey];
    } else if (question && formValues.hasOwnProperty(questionKey)) {
      // pulls the field value out of the form group controls if they exist
      // else just return the question value
      questionValue = this.getFieldValue(formValues[questionKey], question);
    } else {
      questionValue = (formData && formData.data) ? formData.data[questionKey] : null;
    }

    if (questionKeys.length > 1) {
      // this is where I follow the dot notation to get a property of the form value
      // remove the first (parent key)
      let propertyValue = questionValue;
      questionKeys.shift();
      questionKeys.forEach((propertyKey: any) => {
        if (propertyValue) {
          propertyValue = propertyValue[propertyKey];
        }
      });
      questionValue = propertyValue;
    }
    return questionValue;
  }

  mountRepeaterData(formValues: any, formData: any, question: any) {
    let questionData: any = [];

    if (question.fields && question.fields.length) {
      question.fields.forEach((fieldGroup: any) => {
        const groupData: any = {};

        // We now need to gte the original values on the formData object because there are now values
        // on this object that don't get turned into form controls
        // this is just for the multiple repeaters as the regular repeater question
        // maintains this original data as there are no new controla created for these fields

        // First:
        // get the initial value of this field group as it will contain data in it that is not
        // bound to the form object and then build the group data object from that
        // and then, find the id of the field. 
        let flattenedFields = fieldGroup.flat(2);
        let IdField = flattenedFields.find((field: any) => {
          let isIdField = false;
          if (field.key) {
            let fieldKey = field.key.split('__')[1];
            if (fieldKey) {
              let questionKey = fieldKey.split('_')[0];
              isIdField = questionKey === 'id';
            }
          }
          return isIdField;
        });

        // Second:
        // use that object as the groupData
        // Update groupData with that original data from the server
        if (formData.data[question.key] && formData.data[question.key].length) {
          let originalData = formData.data[question.key].find((field: any) => {
            return field.id === IdField.data;
          });
          if (originalData) {
            for (let key in originalData) {
              groupData[question.key + '__' + key] = originalData[key];
            }
          }
        }

        // Lastly:
        // process the input values and put them onto the previously constructed group data object
        fieldGroup.forEach((column: any) => {
          if (column && column.length) {
            column.forEach((field: any) => {
              if (field.key) {
                const fieldValue = formValues[field.key];
                const keyArray = field.key.split('_');
                const fieldKey = keyArray.slice(0, -1).join('_');

                groupData[fieldKey] = fieldValue;
              }
            });
          }
        });
        questionData.push(groupData);
      });
    }
    question.data = questionData;
  }

  // This used to exist in the controls service when I used to pass the field value
  // into the predicate. Now, the same logic is used to look into the controller for
  // controlGroup values on questions and test the predicate against the question value
  // I have the getFieldValue in the control service for now, when we start testing null values
  // on question (which will be shortly) then that function will be removed from the control service
  // and the test against the null value will no longer be required
  getFieldValue(field: any, question: any): any {
    let fieldValue: any = field;
    
    if (question.type === 'date') {
      if (['date_input_3_boxes', 'date_select_month_year'].indexOf(question.layout) > -1) {
        fieldValue = this.getDateValue(field);
      } else if (question.layout === 'range') {
        fieldValue = field.start;
      }
    }

    if (question.type === 'autocomplete') {
      fieldValue = field.value;
    }

    if (question.type === 'validation') {
      fieldValue = this.getValidationValues(fieldValue, question.layout);
    }

    if (question.type === 'autocomplete_multiple' && field.value) {
      // map the values for the autocomplete_multiple question
      fieldValue = field.value.map((value: any) => {
        return value.value;
      });
    }

    if (question.type === 'mobile') {
      fieldValue = this.getMobileNumber(fieldValue);
    }
    
    return fieldValue;
  }

  getValidationValues(value: any, layout: string) {
    let fieldValue;

    if (layout === 'bupa_mem_num') {
      if (value.number && value.suffix) {
        fieldValue = value.number + '-' + value.suffix;
      } else {
        fieldValue = '';
      }
    } else {      
      fieldValue = value;
    }

    return fieldValue;
  }

  getDateValue(value: any) {
    const formatNumber = (num: number) => {
      return num < 10 ? '0' + num : num;
    };
    const day = formatNumber(parseInt(value.day, 10));
    const month = formatNumber(parseInt(value.month, 10));

    return [value.year, month, day].join('-');
  }

  getMobileNumber(value: any) {
    if (!value || !value.mobileNumber || !value.country) {
      return '';
    }

    const normalizedNumber = value.mobileNumber.replace(/^0/, '');
    const mobile = `+${ value.country }${ normalizedNumber }`;

    return mobile;
  }

  // mock form values can be passed into the validator, 
  // otherwise it does a look up of the value from the formData using the question key
  public validator(predicate: any, formValues: any, mockValues?: any): boolean {
    let isValid = false;
    let operator: string;
    let operands: Array<any>;

    if (predicate) {
      operator = predicate.operator;
      operands = [];

      predicate.operands.forEach((operand: any) => {
        if (typeof operand === 'string') {
          if (operand[0] === '<' && operand[operand.length -1] === '>') {
            // will need to also look for pipe function on the operand
            let operandValue = operand.substring(1, operand.length -1);
            let questionKey = operandValue.split('|')[0];
            let questionValue;


            // Check for filter field
            let filterStartIndex = questionKey.indexOf('[');
            let filterEndIndex = questionKey.indexOf(']');
            let filterDetails;

            if (filterStartIndex > -1 && filterEndIndex > -1) {
              filterDetails = questionKey.substring(filterStartIndex + 1, filterEndIndex);
              questionKey = questionKey.split('[')[0];
            }

            // TODO: expand pipe functions to take more than 1 argument
            let questionPipe = operandValue.split('|')[1];
            // this may become a swicth statement in the future when we start getting different
            // predicate constants. It's too soon to tell where this is going to go
            if (RAPTOR_PREDICATE_VARIABLES.indexOf(questionKey) > -1) {
              let formData = this.raptorStepDataService.get();
              questionValue = formData.data[questionKey];
            } else {
              questionValue = this.getQuestionValue(formValues, questionKey, mockValues);
            }

            if (questionPipe) {
              // apply the pipe
              let filteredValue = this.applyPipe(questionValue, questionKey, questionPipe, formValues, mockValues, filterDetails);
              operands.push(filteredValue);
            } else {
              operands.push(questionValue);
            }
          } else {
            operands.push(operand);
          }
        } else if (typeof operand === 'number' || typeof operand === 'boolean') {
          operands.push(operand);
        } else if (Array.isArray(operand)) {
          operands.push(operand);
        } else if (typeof operand === 'object') {
          // this is a predicate
          operands.push(this.validator(operand, formValues, mockValues));
        }
      });

      // NOTE: initial date support was included for the following operators:
      // lt, lte, gt, gte, eq, not_eq
      // If we want to support: 
      // in, any, all then a decision about formatting should be made in order to
      // convert these dates into (maybe date strings YYYY-MM-DD) but it is left out
      // for the time being until we see a real world application of date values being used in these operators
      if (operator[0] === '!') {
        // negated operator
        isValid = !(this as any)['operator_' + operator.substring(1)].call(this, operands);
      } else {
        isValid = (this as any)['operator_' + operator].call(this, operands);
      }
    }

    return isValid;
  }

  private applyPipe(value: any, questionKey: string, pipe: string, formValues: any, mockValues?: any, filterDetails?: any) {
    let result = value;
    let pipeArguments: Array<string> = pipe.split(':');
    let pipeOperator = pipeArguments.shift();

    if (this.isDataDefined(value)) {
      if (pipeOperator === 'ahm_medibank_member_number') {
        let ahm_medibank_mem_number = new RegExp(RAPTOR_AHM_MEDIBANK_MEMBER_NUMBER);
        result = ahm_medibank_mem_number.test(value);
      }

      if (pipeOperator === 'mobile_number_au') {
        let aus_mobile = new RegExp(RAPTOR_MOBILE_NUMBER_AU);
        result = aus_mobile.test(value);
      }

      if (pipeOperator === 'mobile_number_nz') {
        let nz_mobile = new RegExp(RAPTOR_MOBILE_NUMBER_NZ);
        result = nz_mobile.test(value);
      }

      if (pipeOperator === 'mobile_number_uk') {
        let nz_mobile = new RegExp(RAPTOR_MOBILE_NUMBER_UK);
        result = nz_mobile.test(value);
        // valid UK number: 07700 900000
      }

      if (pipeOperator === 'telephone_number_au') {
        let aus_telephone = new RegExp(RAPTOR_TELEPHONE_NUMBER_AU);
        result = aus_telephone.test(value);
      }

      if (pipeOperator === 'telephone_number_nz') {
        let nz_telephone = new RegExp(RAPTOR_TELEPHONE_NUMBER_NZ);
        result = nz_telephone.test(value);
      }

      if (pipeOperator === 'telephone_number_int') {
        let telephone_int = new RegExp(RAPTOR_TELEPHONE_NUMBER_INTERNATIONAL);
        result = telephone_int.test(value);
      }

      if (pipeOperator === 'email_address') {
        let email_address = new RegExp(RAPTOR_EMAIL_ADDRESS);
        result = email_address.test(value);
      }

      if (pipeOperator === 'count') {
        result = this.pipeCount(value, questionKey, filterDetails);
      }

      if (pipeOperator === 'sum') {
        result = this.pipeSum(value, pipeArguments[0], questionKey, filterDetails);
      }

      if (pipeOperator === 'multiply') {
        result = this.pipeMultiply(value, pipeArguments[0]);
      }

      if (pipeOperator === 'age') {
        result = this.pipeAge(value, formValues, mockValues, undefined, pipeArguments);
      }

      if (pipeOperator === 'bsk_age_count') {
        result = this.pipeBskAgeCount(value, formValues, mockValues, pipeArguments);
      }

      if (pipeOperator === 'check_luhn') {
        result = this.luhnCheck(value, pipeArguments);
      }
    }

    return result;
  }

  private pipeCount(value: any, questionKey: string, filterDetails?: any): any {
    let result = 0;

    if (Array.isArray(value)) {
      if (filterDetails) {
        let filteredResult = this.repeaterValueFilter(value, questionKey, filterDetails);
        result = filteredResult.length;
      } else {
        result = value.length;
      }
    } else if (typeof value === 'string') {
      result = value.length;
    }

    return result;
  }

  private pipeSum(value: any, columnToSum: string, questionKey: string, filterDetails?: any): any {
    let result = 0;

    if (Array.isArray(value) && columnToSum) {
      let filteredResult = value;
      if (filterDetails) {
        filteredResult = this.repeaterValueFilter(value, questionKey, filterDetails);
      }
      if (filteredResult && filteredResult.length) {
        let columnName = `${columnToSum}`;
        if (filteredResult[0].hasOwnProperty(`${questionKey}__${columnToSum}`)) {
          columnName = `${questionKey}__${columnToSum}`;
        }
        result = filteredResult.reduce((previousValue, currentValue) => previousValue + this.removeNumberFormatting(currentValue[columnName]), 0);
      }
    }

    return result;
  }

  private pipeMultiply(value: any, otherValue: any): any {
    let result;

    if (otherValue) {
      result = value * this.removeNumberFormatting(otherValue);
    }

    return result;
  }

  private pipeAge(value: any, formValues: any, mockValues: any, referenceDate = 'ref_date', args?: Array<string>): any {
    let result;
    let argsQuestion;
    let argsUnit;
    let formData = this.raptorStepDataService.get();

    // if there are arguments on the age pipe then apply them
    if (args && args.length) {
      if (args[0] === RAPTOR_PREDICATE_CURRENT_DATE) {
        argsQuestion = formData.data[RAPTOR_PREDICATE_CURRENT_DATE];
      } else {
        argsQuestion = this.getQuestionValue(formValues, args[0], mockValues);
      }

      if (args && args.length === 2) {
        argsUnit = args[1];
        // check for the first variable being empty
        // if so, then use today's date as the refDate comparator
        if (args[0] === '') {
          argsQuestion = new Date();
        }
      }
    }

    if (value) {
      let comparatorValue: any;
      let answerValue = this.convertToDate(value);
      let refDate = (formData && formData.data && formData.data.referenceDate);

      if (argsQuestion) {
        refDate = argsQuestion;
      }

      comparatorValue = refDate ? this.convertToDate(refDate) : new Date();

      if (answerValue) {
        if (argsUnit === 'days') {
          result = Math.round((answerValue - comparatorValue) / (1000 * 60 * 60 * 24));
        } else if (argsUnit === 'age') {
          result = getAge(answerValue, comparatorValue);
        } else if (argsQuestion) { // argsUnit === 'year' is implied here
          result = getAge(comparatorValue, answerValue);
        } else {
          result = getAge(answerValue, comparatorValue);
        }
      }
    }

    function getAge(value: any, comparator: any): number {
      let age;

      let comparatorYear = comparator.getFullYear();
      let comparatorMonth = comparator.getMonth();
      let comparatorDay = comparator.getDate();

      let valueYear = value.getFullYear();
      let valueMonth = value.getMonth();
      let valueDay = value.getDate();

      // take difference between years
      age = comparatorYear - valueYear;
      // check months
      if (comparatorMonth === valueMonth) {
        // if months are the same then check days
        if (comparatorDay < valueDay) {
          // if comparatorDay is before answerDay then remove 1 year
          age = age - 1;
        }
      } else {
        if (comparatorMonth < valueMonth) {
          // if comparatorMonth is before answerMonth then remove 1 year
          age = age - 1;
        }
      }
      return age;
    }

    return result;
  }

  private repeaterValueFilter(value: any, questionKey: string, details: any) {
    let column = details.split('=')[0].trim();
    let searchValue = this.applyBooleanFormatting(details.split('=')[1].trim());

    return value.filter((item: any) => { return item[`${questionKey}__${column}`] === searchValue || item[`${column}`] === searchValue });
  }


  private pipeBskAgeCount(value: any, formValues: any, mockValues: any, pipeArguments: Array<string>) {
    let count = 0;
    let fieldKey = pipeArguments[0]; 
    let refDate = pipeArguments[1];
    let operator = pipeArguments[2];
    let age = pipeArguments[3];
    
    if (Array.isArray(value)) {
      let ageValue;

      value.forEach((row: any) => {
        let field = row[fieldKey];

        if (field) {
          ageValue = this.pipeAge(field, formValues, mockValues, refDate);
          if (ageValue) {
            let isTrue = (this as any)['operator_' + operator].call(this, [ageValue, age]);
            if (isTrue) {
              count = count + 1;
            }
          }
        }
      }); 
    }
    return count;
  }

  private luhnCheck(value: any, pipeArguments: Array<string>): boolean {
    if (value.length !== parseInt(pipeArguments[0])) return false;
    // Accept only digits, dashes or spaces
    if (/[^0-9-\s]+/.test(value)) return false;

    let sum = 0;
    value = value.replace(/\D/g, '');
    let i = value.length - 1;

    for (i; i >= 0; i--) {
      let digit = value.charAt(i);
      let number = parseInt(digit, 10);

      if (i % 2 === 1) {
        number = number * 2;
        if (number > 9) {
          number = number - 9;
        }
      }
      sum = sum + number;
    }

    return (sum % 10) === 0;
  }

  private operator_eq(operands: Array<any>) {
    let valid = false;
    let areDates = this.areOperandsDateValues(operands);

    if (areDates) {
      valid = this.operator_date_eq(operands); 
    } else {
      if (typeof operands[0] === 'boolean' && typeof operands[1] === 'string') {
        operands[0] = operands[0].toString();
      }
      valid = operands[0] === operands[1];
    }
    return valid;
  }

  private operator_not_eq(operands: Array<any>) {
    let valid = false;
    let areDates = this.areOperandsDateValues(operands);

    if (areDates) {
      valid = !this.operator_date_eq(operands);
    } else {
      if (typeof operands[0] === 'boolean' && typeof operands[1] === 'string') {
        operands[0] = operands[0].toString();
      }
      valid = operands[0] !== operands[1];
    }
    return valid;
  }

  private operator_in(operands: Array<any>) {
    let value = operands[0];
    let collection = operands[1];
    let result = false;

    if (typeof collection === 'string') {
      collection = JSON.parse(collection);
    }

    if (typeof collection === 'object') {
      if (Array.isArray(collection)) {
        result = collection.indexOf(value) > -1;
      } else {
        result = operands[1][operands[0]];
      }
    }
    return !!result;
  }

  private operator_any(operands: Array<any>) {
    let hasValue = false;

    operands.forEach((operand: any) => {
      if (!!operand) {
        hasValue = true;
      }
    });
    return hasValue;
  }

  private operator_all(operands: Array<any>) {
    let valid = true;

    operands.forEach((operand: any) => {
      if (!operand) {
        valid = false;
      }
    });
    return valid;
  }

  private operator_lt(operands: Array<any>) {
    let valid = false;
    let areDates = this.areOperandsDateValues(operands);

    if (this.isDataDefined(operands[0]) && this.isDataDefined(operands[1])) {
      if (areDates) {
        valid = this.operator_date_lt(operands);
      } else {
        valid = this.removeNumberFormatting(operands[0]) < this.removeNumberFormatting(operands[1]);
      }
    }
    return valid;
  }

  private operator_lte(operands: Array<any>) {
    let valid = false;
    let areDates = this.areOperandsDateValues(operands);

    if (this.isDataDefined(operands[0]) && this.isDataDefined(operands[1])) {
      if (areDates) {
        valid = this.operator_date_lte(operands);
      } else {
        valid = this.removeNumberFormatting(operands[0]) <= this.removeNumberFormatting(operands[1]);
      }
    }

    return valid;
  }

  private operator_gt(operands: Array<any>) {
    let valid = false;
    let areDates = this.areOperandsDateValues(operands);

    if (this.isDataDefined(operands[0]) && this.isDataDefined(operands[1])) {
      if (areDates) {
        valid = this.operator_date_gt(operands);
      } else {
        valid = this.removeNumberFormatting(operands[0]) > this.removeNumberFormatting(operands[1]);
      }
    }

    return valid;
  }

  private operator_gte(operands: Array<any>) {
    let valid = false;
    let areDates = this.areOperandsDateValues(operands);

    if (this.isDataDefined(operands[0]) && this.isDataDefined(operands[1])) {
      if (areDates) {
        valid = this.operator_date_gte(operands);
      } else {
        valid = this.removeNumberFormatting(operands[0]) >= this.removeNumberFormatting(operands[1]);
      }
    }

    return valid;
  }

  private operator_date_eq(operands: Array<any>) {
    let answer = operands[0];
    let comparator = operands[1];
    let answerValue;
    let comparatorValue;
    let isValid = false;

    if (answer) {
      answerValue = this.convertToDate(answer);
      comparatorValue = this.convertToDate(comparator);
      if (answerValue && comparatorValue) {
        isValid = answerValue.getTime() === comparatorValue.getTime();
      }
    }
    return isValid;
  }

  private operator_date_lt(operands: Array<any>) {
    let answer = operands[0];
    let comparator = operands[1];
    let answerValue;
    let comparatorValue;
    let isValid = false;

    if (answer) {
      answerValue = this.convertToDate(answer);
      comparatorValue = this.convertToDate(comparator);
      if (answerValue && comparatorValue) {
        isValid = answerValue < comparatorValue;
      }
    }
    return isValid;
  }

  private operator_date_lte(operands: Array<any>) {
    let answer = operands[0];
    let comparator = operands[1];
    let answerValue;
    let comparatorValue;
    let isValid = false;

    if (answer) {
      answerValue = this.convertToDate(answer);
      comparatorValue = this.convertToDate(comparator);
      if (answerValue && comparatorValue) {
        isValid = answerValue <= comparatorValue;
      }
    }
    return isValid;
  }

  private operator_date_gt(operands: Array<any>) {
    let answer = operands[0];
    let comparator = operands[1];
    let answerValue;
    let comparatorValue;
    let isValid = false;

    if (answer) {
      answerValue = this.convertToDate(answer);
      comparatorValue = this.convertToDate(comparator);
      if (answerValue && comparatorValue) {
        isValid = answerValue > comparatorValue;
      }
    }
    return isValid;
  }

  private operator_date_gte(operands: Array<any>) {
    let answer = operands[0];
    let comparator = operands[1];
    let answerValue;
    let comparatorValue;
    let isValid = false;

    if (answer) {
      answerValue = this.convertToDate(answer);
      comparatorValue = this.convertToDate(comparator);
      if (answerValue && comparatorValue) {
        isValid = answerValue >= comparatorValue;
      }
    }
    return isValid;
  }

  public convertToDate(answer: any): any {
    if (answer instanceof Date) {
      return answer;
    } else if (answer && answer._isAMomentObject) {
      return answer.toDate();
    } else if (answer && answer.year && answer.month && answer.day) {
      // day/month/year field
      if (this.isValidDateObject(answer)) {
        return new Date(answer.year, answer.month - 1, answer.day);
      }
    } else if (typeof answer === 'string' && this.isValidDateObject(answer)) {
      const newdate = new Date(answer);
      newdate.setHours(0,0,0,0); //Set time to midnight
      return newdate;
    }
    return null;
  }

  // expects a day/month/year object but can also be a date string 'YYYY-MM-DD'
  private isValidDateObject(answer: any) {
    const day30 = [4, 6, 9, 11];
    const day31 = [1, 3, 5, 7, 8, 10, 12];
    let answerObject = answer;

    if (typeof answer === 'string') {
      let dateArray = answer.split('-');
      if (dateArray.length === 3) {
        answerObject = {
          year: dateArray[0],
          month: dateArray[1],
          day: dateArray[2]
        };
      } else {
        return false;
      }
    }

    let day = parseInt(answerObject.day);
    let month = parseInt(answerObject.month);
    let year = parseInt(answerObject.year);

    let dayValid = false;
    let monthValid = month <= 12;
    let yearValid = year >= 1000 && year < 10000;


    if (monthValid && yearValid) {
      if (day30.indexOf(month) > -1) {
        dayValid = day <= 30;
      }
      if (day31.indexOf(month) > -1) {
        dayValid = day <= 31;
      }
      if (month === 2) {
        let leapYear = false;
        if (year % 400 === 0) {
          leapYear = true;
        } else if (year % 100 === 0) {
          leapYear = false;
        } else if (year % 4 === 0) {
          leapYear = true;
        }
        dayValid = leapYear ? day <= 29 : day <= 28;
      }
    }

    return dayValid && monthValid && yearValid;
  }

  private areOperandsDateValues(operands: Array<any>): boolean {
    let areDateValues = true;
    operands.forEach((operand: any) => {
      // if this operand is not a date then set the areDateValues to false
      if (
        !(
          operand &&
          (
            operand instanceof Date ||
            operand._isAMomentObject ||
            (typeof operand === 'string' && this.isValidDateObject(operand)) ||
            (operand.year && operand.month && operand.day)
          )
        )
      ) {
        areDateValues = false;
      }
    });
    return areDateValues;
  }

  private applyBooleanFormatting(value: any): any {
    let formattedValue = value;
    if (typeof value === 'string') {
      if (value.toLowerCase() === 'true') {
        formattedValue = true;
      }
      if (value.toLowerCase() === 'false') {
        formattedValue = false;
      }
    }
    return formattedValue;
  }

  private removeNumberFormatting(value: any): any {
    if (typeof value === 'string') {
      return parseFloat(value.replace(/[^0-9.]/g, ''));
    }
    return parseFloat(value);
  }
}
